/* Notification Center — Rule Detail page, ported from the standalone HTML
 * prototype's #page-rule-detail.  Opened by Edit (1 row selected) or by clicking
 * a rule name on the Notification Rules list.  8 tabs: General · Trigger
 * Condition · Targets · Recipients · Delivery Method · Escalation · Email
 * Template · Audit/History.  Demo data — the conditions are built-in MDI
 * defaults; the one configurable thing is recipients.
 *
 * TARGETS tab (2026-09-19 redesign): a master/detail expandable grid driven by
 * the Permit Library (permitLibrary.ts) — the source of truth.  One main row per
 * parameter/analyte per permit (never duplicated for its multiple limit types),
 * expanding to a sub-grid of that parameter's configured limits.  No limit value
 * is entered here; everything resolves from the Permit Library. */
import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNotify } from '../../components/notifications';
import { useConfirm } from '../../components/confirm';
import { useHotkeys, formatCombo } from '../../components/hotkeys';
import {
  PURPOSE_LABEL, SEVERITY_LABEL,
  parseRuleConfig, recipientGroupIdsOf, groupRefsFor, emailTemplateIdOf,
  type NcRule, type Severity, type NcRuleConfig,
} from './data';
import { getHoldTimeCatalog, getPermitLibrary } from './api';
import { buildPermitTargets, type PlPermit } from './permitLibrary';
import { PermitTargetsGrid } from './PermitTargetsGrid';
import { HoldTimeTargetsGrid } from './HoldTimeTargetsGrid';
import {
  holdTimeTargetsError, selFromTargets, toSavedTargets,
  type HoldTimeCatalogRow, type HoldTimeSel,
} from './holdTime';
import { RecipientGroupPicker } from './RecipientGroupPicker';
import { EmailTemplatePicker } from './EmailTemplatePicker';
import { enabledTriggerForms, triggerFormById, triggersEvaluationText } from './triggerForms';
import { useNcStore } from './store';
import { HOLD_TIME_CATEGORY_CODE, categoryCodeLabel, ruleDescription } from './categoryCodes';
import { incompleteMessage, ruleMissing } from './ruleCompleteness';
import { AuditTrailButton } from '../../components/AuditTrailButton';
import { NotificationRulesAuditForm } from './data';
import { DeliveryMethodFields } from './DeliveryMethodFields';
import type { EscalationValue } from './escalation';
import {
  DELAY_LABEL, DELAY_STORED, PERMIT_EXCEEDANCE_CATEGORY_CODE, effectiveAck, deliveryError,
  type DeliveryValue, type DelayLabel, type RepeatUnit,
} from './deliveryMethod';

const SEVERITY_OPTIONS: Severity[] = ['critical', 'warning', 'info'];

type Tab = 'general' | 'trigger' | 'targets' | 'recipients' | 'delivery' | 'template';
const TABS: { key: Tab; icon: string; label: string }[] = [
  { key: 'general', icon: 'ti-info-circle', label: 'General' },
  { key: 'trigger', icon: 'ti-bolt', label: 'Trigger Condition' },
  { key: 'targets', icon: 'ti-target', label: 'Targets' },
  { key: 'recipients', icon: 'ti-users-group', label: 'Recipients' },
  { key: 'delivery', icon: 'ti-send', label: 'Delivery Method' },
  { key: 'template', icon: 'ti-mail', label: 'Email Template' },
];

/** The Name column is nvarchar(200). */
const MAX_RULE_NAME = 200;
/** A duplicate's name: "Copy of {name}", within the column size. */
function copyName(name: string) { return `Copy of ${name}`.slice(0, MAX_RULE_NAME); }

function SevPill({ s }: { s: Severity }) {
  const label = s === 'critical' ? 'Critical' : s === 'warning' ? 'Warning' : 'Info';
  return <span className={`nc-sev ${s}`}><span className="dot" />{label}</span>;
}
function Info({ label, children }: { label: string; children: ReactNode }) {
  return <div className="nc-info-item"><div className="nc-ii-label">{label}</div><div className="nc-ii-value">{children}</div></div>;
}
export default function RuleDetailPage({ rule, categoryName, onBack, onDeleted, onRefresh, initialMode = 'view' }: {
  rule: NcRule; categoryName: string; onBack: () => void; onDeleted: () => void;
  /** Reload this rule from the database (the page re-opens with the fresh values). */
  onRefresh?: () => void;
  /** 'edit' = the list toolbar Edit opened the rule: start in Edit mode (RTCFR 2026-10-16).
   *  'view' (default) = a click on the rule name. Read on open only. */
  initialMode?: 'view' | 'edit';
}) {
  const notify = useNotify();
  const { confirm } = useConfirm();
  const patchRule = useNcStore((s) => s.patchRule);
  const liveGroups = useNcStore((s) => s.recipients);
  const liveTemplates = useNcStore((s) => s.templates);
  const [tab, setTab] = useState<Tab>('general');
  const [active, setActive] = useState(rule.enabled);
  // Editable header/general fields (persisted columns) + config-backed fields.
  const [name, setName] = useState(rule.name);
  const [severity, setSeverity] = useState<Severity | undefined>(rule.severity);
  // Recipients — predefined Recipient GROUPS (not ad-hoc roles). This holds the
  // selected group IDS (the stable link — dbo.AI_NotificationRuleRecipientGroup);
  // every name shown is looked up LIVE by id, so a renamed group shows its new
  // name here and in the picker, never "Group no longer exists".
  const [recipientGroups, setRecipientGroups] = useState<string[]>(() => recipientGroupIdsOf(rule, useNcStore.getState().recipients));
  // Email Template — the linked template's ID (AI_NotificationRule.EmailTemplateId),
  // a reference, never a copy; null = no template (built-in email). Its name is
  // always looked up LIVE by id, so a renamed template shows its new name.
  const [emailTemplate, setEmailTemplate] = useState<string | null>(() => emailTemplateIdOf(rule, useNcStore.getState().templates));
  // Audit: the rule's changes are written by the SERVER to dbo.AuditData (FormName "Notification Rules") on every
  // save — one row per changed value — and shown by the shared Audit chip in the header, like every other form
  // (owner request 2026-10-24; replaces the browser-built Audit / History tab).
  // TARGETS — the FULL live Permit Library (source of truth) fetched from the API,
  // one row per parameter/permit. EVERY configured parameter is listed (not just a
  // rule's selected ones); the rule owns only the per-parameter Enabled toggle and
  // which rows are expanded — the limits themselves are never edited here.
  const [library, setLibrary] = useState<PlPermit[] | null>(null);
  const targetsLoading = library === null;
  useEffect(() => {
    let alive = true;
    getPermitLibrary().then((l) => { if (alive) setLibrary(l); }).catch(() => { if (alive) setLibrary([]); });
    return () => { alive = false; };
  }, []);
  const permitTargets = useMemo(() => buildPermitTargets(library ?? []), [library]);
  // The rule's SAVED ON/OFF set (rule.targetKeys), seeded once the library loads.
  // A rule WITH saved keys shows exactly those ON and every other parameter OFF;
  // a legacy rule with none defaults every parameter ON. Never resets a selection.
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  useEffect(() => {
    if (library === null) return;
    const seeded = Object.fromEntries(permitTargets.map((t) => [t.key, rule.targetKeys ? rule.targetKeys.includes(t.key) : true]));
    setEnabled(seeded);
    // Edit clicked before the library loaded → the draft was seeded empty; fill
    // it now so the grid and the Save see the same complete ON/OFF map.
    setTgtDraft((cur) => (Object.keys(cur).length ? cur : seeded));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [library]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Page-level VIEW/EDIT: the whole Rule Detail is READ-ONLY until Edit is
  // clicked; Save persists and returns to read-only, Cancel discards and returns.
  // Targets follow the same flag — View shows only enabled targets, Edit shows
  // every Permit Library parameter with the saved ON/OFF states on a DRAFT copy.
  const [tgtDraft, setTgtDraft] = useState<Record<string, boolean>>({});
  // HOLD TIME category (RTCFR 2026-10-14): the Targets tab is the Test Method grid
  // (Matrix · Test · Method · Hold Time · Alert · Warn Before) over the rule's saved
  // holdTimeTargets; the Permit Library grid below is untouched for every other
  // category. htSaved = what is stored (View); htDraft = the Edit copy.
  // The rule's category is identified by its CODE, never its name (owner decision 2026-10-23): RC-01 Result
  // Exceedance (Permit Library parameters + limits; Suppress duplicate firing applies only here — hold-time warnings
  // fire once per threshold by design) and RC-02 Hold Time (the tests' hold times).
  const ruleCategoryCode = useNcStore((s) => categoryCodeLabel(
    s.categories.find((x) => x.rules.some((r) => r.code === rule.code))
    ?? s.categories.find((x) => x.name === categoryName)));
  const isHoldTime = ruleCategoryCode === HOLD_TIME_CATEGORY_CODE;
  // A Hold Time rule has no Trigger Condition tab: it has no trigger forms, and its tests are on Targets.
  const visibleTabs = isHoldTime ? TABS.filter((t) => t.key !== 'trigger') : TABS;
  const isPermitExceedance = ruleCategoryCode === PERMIT_EXCEEDANCE_CATEGORY_CODE;
  const [htCatalog, setHtCatalog] = useState<HoldTimeCatalogRow[] | null>(null);
  useEffect(() => {
    if (!isHoldTime) return;
    let alive = true;
    getHoldTimeCatalog().then((rows) => { if (alive) setHtCatalog(rows); }).catch(() => { if (alive) setHtCatalog([]); });
    return () => { alive = false; };
  }, [isHoldTime]);
  const [htSaved, setHtSaved] = useState<Record<string, HoldTimeSel>>(() => selFromTargets(rule.holdTimeTargets));
  const [htDraft, setHtDraft] = useState<Record<string, HoldTimeSel>>({});
  const htSavedCount = Object.values(htSaved).filter((v) => v.enabled).length;
  const enabledCount = isHoldTime ? htSavedCount : permitTargets.filter((t) => enabled[t.key]).length;
  const viewTargets = permitTargets.filter((t) => enabled[t.key]);
  // TRIGGER — the workflow forms/events this rule fires on (multi-select).
  // Loaded from the saved rule (falls back to result-validation for legacy rules
  // with none).  A saved trigger now disabled in Default Settings is KEPT, shown
  // as a flagged row, and stays selectable — never silently changed.
  // A Hold Time rule has NO trigger forms (the background hold-time scan raises it — the New Rule wizard
  // has no Trigger step for it), so none are loaded: the fallback below once gave it a stored
  // "result-validation" trigger that applied to nothing, which its next Save now clears.
  const savedTriggers = isHoldTime ? [] : rule.triggers?.length ? rule.triggers : ['result-validation'];
  const [triggerSel, setTriggerSel] = useState<Record<string, boolean>>(
    () => Object.fromEntries(savedTriggers.map((id) => [id, true])));
  // Rows: every Default-Settings-enabled form, plus any saved trigger that is now
  // disabled (so it is never dropped from view).
  const triggerRows = useMemo(() => {
    const list = [...enabledTriggerForms()];
    for (const id of Object.keys(triggerSel)) {
      const f = triggerFormById(id);
      if (f && !list.some((x) => x.id === f.id)) list.push(f);
    }
    return list;
  }, [triggerSel]);
  const selectedTriggerIds = triggerRows.filter((f) => triggerSel[f.id]).map((f) => f.id);
  // A saved+selected trigger whose form is disabled in Default Settings.
  const disabledSelected = triggerRows.filter((f) => triggerSel[f.id] && !f.enabled);
  // General governance toggles
  // Requires acknowledgment — AI_NotificationRule.Ack. A rule saved with it OFF arrives as
  // undefined, so read it as a strict boolean (it used to default ON and be re-saved ON).
  // Only an In-App alert can be acknowledged: with In-App off it is not required (2026-10-23).
  const [gov, setGov] = useState({ ack: effectiveAck(rule.notifyInApp, rule.ack) });
  // The SAVED value — drives the header chip and the Delivery Method note, so they show what
  // is stored (not the unsaved toggle) and follow a Save without reloading the page.
  const [savedAck, setSavedAck] = useState(effectiveAck(rule.notifyInApp, rule.ack));
  // Delivery / escalation toggles
  // All three are SAVED: AI_NotificationRule.AutoResolve (2026-10-19) and SuppressDuplicates / ClientVisible
  // (2026-10-23 — until then these two reset to on / off on every reload). Bypass digest batching was removed 2026-10-24.
  const [del, setDel] = useState({
    suppress: rule.suppressDuplicates ?? true,
    portal: rule.clientVisible ?? false, autoResolve: !!rule.autoResolve,
  });
  // Delay + Business hours are SAVED (AI_NotificationRule.DelayMode / BusinessHoursOnly, 2026-10-19) and the
  // server holds / batches the alerts. Repeat until acknowledged is SAVED too (RepeatEnabled / RepeatEvery /
  // RepeatUnit, 2026-10-20) and the server re-sends the alert as "Reminder N"; it sits in the In-App alert
  // group because acknowledging in the bell is what stops it.
  const [delay, setDelay] = useState<DelayLabel>(DELAY_LABEL[rule.delayMode ?? 'Immediate']);
  const [bizHours, setBizHours] = useState(!!rule.businessHoursOnly);
  // Escalation (SAVED): the users it goes to (2026-10-24), or a Recipient Group set up before then.
  const [esc, setEsc] = useState<EscalationValue>({
    userIds: rule.escalationUserIds ?? [], groupId: rule.escalationGroupId ?? '', after: rule.escalateAfter ?? '1h',
  });
  const [repeat, setRepeat] = useState<{ on: boolean; value: string; unit: RepeatUnit }>({
    on: !!rule.repeatEnabled, value: String(rule.repeatEvery ?? 4), unit: rule.repeatUnit === 'Minutes' ? 'Minutes' : 'Hours',
  });
  // Delivery Method channels that are SAVED on the rule (AI_NotificationRule.NotifyInApp /
  // NotifyEmail). Dashboard / SMS / Teams are not sent by anything yet and stay as before.
  const [chan, setChan] = useState({ inApp: rule.notifyInApp ?? false, email: rule.notifyEmail ?? true });
  // Pop-up setting for this rule's in-app alerts (AI_NotificationRule.PopupSettingId): '' = follow the
  // Default. The list is the named settings from Notification Center → Pop-up Settings.
  const [popupSetting, setPopupSetting] = useState<string>(rule.popupSettingId ?? '');
  const popupSnap = useRef<string>(rule.popupSettingId ?? '');

  // The Delivery Method fields are the shared DeliveryMethodFields (also the New Rule wizard's step); this
  // page keeps its own state pieces, which the history, Cancel and Save below already read.
  const deliveryValue: DeliveryValue = {
    inApp: chan.inApp, email: chan.email, popupSetting, ack: gov.ack, repeat, delay, bizHours,
    suppress: del.suppress, portal: del.portal, autoResolve: del.autoResolve,
  };
  function setDelivery(v: DeliveryValue) {
    setChan({ inApp: v.inApp, email: v.email });
    setPopupSetting(v.popupSetting);
    setGov((g) => ({ ...g, ack: v.ack }));
    setRepeat(v.repeat);
    setDelay(v.delay);
    setBizHours(v.bizHours);
    setDel((d) => ({ ...d, suppress: v.suppress, portal: v.portal, autoResolve: v.autoResolve }));
  }

  // What this rule still needs before it can be ON, counted from what is on screen (the Edit drafts).
  function draftMissing(): string[] {
    const targetCount = isHoldTime
      ? Object.values(htDraft).filter((v) => v.enabled).length
      : (Object.keys(tgtDraft).length ? Object.values(tgtDraft).filter(Boolean).length : (rule.targetKeys?.length ?? 0));
    return ruleMissing({
      categoryCode: ruleCategoryCode,
      triggerCount: selectedTriggerIds.length,
      targetCount,
      recipientCount: groupRefsFor(recipientGroups, liveGroups, rule.recipientGroups).filter((g) => g.exists).length,
      inApp: chan.inApp,
      email: chan.email,
    });
  }
  /** Turning the rule ON needs it complete; turning it OFF is always allowed. */
  function toggleActive() {
    if (!active) {
      const missing = draftMissing();
      if (missing.length) { notify.warning(incompleteMessage(missing)); return; }
    }
    setActive(!active);
  }

  // ── Page read-only / edit gate ─────────────────────────────────────────────
  const [isEditing, setIsEditing] = useState(false);
  // The Permit Library parameters with their limits — ONE render shown on both Trigger Condition (RC-01) and
  // Targets, so the two tabs edit the same draft. View = the enabled parameters; Edit = every parameter.
  const permitGrid = targetsLoading
    ? <div className="nc-state-row"><span className="nc-inline-spin" /> Loading Permit Library parameters…</div>
    : isEditing
      ? <PermitTargetsGrid targets={permitTargets} enabled={tgtDraft} onToggle={(key) => toggleDraftTarget(key)}
          expanded={expanded} onExpand={toggleExpand} showSearch />
      : <PermitTargetsGrid targets={viewTargets} enabled={enabled} onToggle={() => {}} expanded={expanded} onExpand={toggleExpand}
          readOnly emptyText="No targets enabled — click Edit to choose parameters to monitor." />;
  // The Hold Time test grid (RC-02) — shown on Targets only; a Hold Time rule has no Trigger Condition tab.
  const holdTimeGrid = (
    <HoldTimeTargetsGrid
      catalog={htCatalog ?? []}
      saved={rule.holdTimeTargets}
      loading={htCatalog === null}
      sel={isEditing ? htDraft : htSaved}
      readOnly={!isEditing}
      onChange={(id, next) => setHtDraft((cur) => ({ ...cur, [id]: next }))}
    />
  );
  // Bumped on Cancel to remount the tab panels so the uncontrolled inputs
  // (delivery / escalation / template selects & checkboxes) revert to defaults.
  const [formKey, setFormKey] = useState(0);
  // Snapshot of the controlled fields taken when Edit starts, restored on Cancel.
  const snapRef = useRef<{
    active: boolean; gov: typeof gov; del: typeof del; chan: typeof chan; triggerSel: typeof triggerSel;
    delay: typeof delay; repeat: typeof repeat; bizHours: boolean; esc: typeof esc;
    name: string; severity?: Severity; recipientGroups: string[]; emailTemplate: string | null;
  } | null>(null);

  function toggleExpand(key: string) {
    setExpanded((cur) => ({ ...cur, [key]: !cur[key] }));
  }
  function toggleDraftTarget(key: string) { setTgtDraft((cur) => ({ ...cur, [key]: !cur[key] })); }

  // Enter edit: snapshot every editable field (deep copy of recipients) and seed
  // the Targets draft so all tabs edit against the current values.
  function startEdit() {
    snapRef.current = {
      active, gov: { ...gov }, del: { ...del }, chan: { ...chan }, triggerSel: { ...triggerSel },
      delay, repeat: { ...repeat }, bizHours, esc: { ...esc },
      name, severity, recipientGroups: [...recipientGroups], emailTemplate,
    };
    popupSnap.current = popupSetting;
    // A COMPLETE map (every library parameter, explicit true/false) — never a
    // sparse copy, or unseeded keys would render ON yet save as OFF.
    setTgtDraft(Object.fromEntries(permitTargets.map((t) => [t.key, enabled[t.key] === true])));
    setHtDraft(Object.fromEntries(Object.entries(htSaved).map(([id, v]) => [id, { enabled: v.enabled, warn: [...v.warn] }])));
    setIsEditing(true);
  }
  // Opened from the list toolbar Edit → enter Edit mode through the SAME startEdit the
  // in-page Edit button uses (same snapshot, so Cancel / Save behave identically). Once,
  // on open, before the first paint (no View-mode flash); later data loads and
  // re-renders never touch isEditing. A Permit Library that arrives afterwards fills the
  // still-empty Targets draft (see the library effect above).
  const openedInEdit = useRef(false);
  useLayoutEffect(() => {
    if (initialMode !== 'edit' || openedInEdit.current) return;
    openedInEdit.current = true;
    startEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Cancel: restore the snapshot, drop the Targets draft, remount the panels so
  // uncontrolled inputs revert, and return to read-only.
  function cancelEdit() {
    const s = snapRef.current;
    if (s) {
      setActive(s.active); setGov(s.gov); setDel(s.del); setChan(s.chan); setTriggerSel(s.triggerSel);
      setDelay(s.delay); setRepeat(s.repeat); setBizHours(s.bizHours); setEsc(s.esc);
      setName(s.name); setSeverity(s.severity); setRecipientGroups([...s.recipientGroups]); setEmailTemplate(s.emailTemplate);
      setPopupSetting(popupSnap.current);
    }
    setTgtDraft({});
    setHtDraft({});
    setIsEditing(false);
    setFormKey((k) => k + 1);
    notify.info('Changes discarded.');
  }

  // Save: confirm, then persist name / severity / enabled / recipients / email
  // template / targets in ONE write, appending the real change history.
  function saveEdit() {
    // Hold Time: at least one Alert on and every Warn Before value valid — checked
    // BEFORE the confirm so the user stays in Edit with every value kept.
    if (isHoldTime) {
      const err = htCatalog === null ? 'Hold-time tests are still loading.' : holdTimeTargetsError(htDraft, htCatalog);
      if (err) { notify.warning(err); setTab('targets'); return; }
    }
    // Repeat until acknowledged: a usable interval when it is ticked (the server checks the same rule).
    const deliveryErr = deliveryError(deliveryValue);
    if (deliveryErr) { notify.warning(deliveryErr); setTab('delivery'); return; }
    // An ACTIVE rule must be complete (owner request 2026-10-24) — the server refuses it otherwise.
    const missing = active ? draftMissing() : [];
    if (missing.length) { notify.warning(incompleteMessage(missing)); return; }
    void confirm({ title: 'Save', message: 'Do you want to Save?' }).then((ok) => {
      if (!ok) return;
      // A Hold Time rule has no Permit Library targets (its targets are the tests).
      const newKeys = isHoldTime ? [] : permitTargets.filter((t) => tgtDraft[t.key]).map((t) => t.key);
      const htTargets = isHoldTime ? toSavedTargets(htDraft, htCatalog ?? []) : undefined;
      const htOn = htTargets?.filter((t) => t.enabled).length ?? 0;
      // Groups persist by ID (recipientGroups refs); the names in config are only
      // the display snapshot — the server links by id, never by name.
      const groupRefs = groupRefsFor(recipientGroups, liveGroups, rule.recipientGroups);
      const liveNames = groupRefs.filter((g) => g.exists).map((g) => g.name);
      // The template persists by ID (emailTemplateId); the name in config is only
      // the display snapshot — the server links by id, never by name.
      const tplName = emailTemplate ? liveTemplates.find((t) => t.id === emailTemplate)?.name : undefined;
      const cfg: NcRuleConfig = { ...parseRuleConfig(rule), recipients: undefined, recipientGroups: liveNames, emailTemplate: tplName, history: undefined };
      const cleanName = name.trim() || rule.name;
      // commit local view state
      setName(cleanName);
      setSavedAck(effectiveAck(chan.inApp, gov.ack));
      setEnabled(tgtDraft);
      if (isHoldTime) setHtSaved(selFromTargets(htTargets));
      // persist (write-through)
      patchRule(rule.code, {
        name: cleanName,
        severity,
        enabled: active,
        // Requires acknowledgment (AI_NotificationRule.Ack) — was shown but never written.
        ack: effectiveAck(chan.inApp, gov.ack),
        recipients: liveNames.join(', '),
        recipientGroups: groupRefs,
        emailTemplateId: emailTemplate,
        // Untouched = keep the stored link; changed to "Default" = '' (clears it); changed to a setting = its id.
        popupSettingId: popupSetting === (rule.popupSettingId ?? '') ? (rule.popupSettingId ?? null) : popupSetting,
        targets: isHoldTime
          ? (htOn ? `${htOn} target${htOn === 1 ? '' : 's'}` : undefined)
          : (newKeys.length ? `${newKeys.length} target${newKeys.length === 1 ? '' : 's'}` : undefined),
        targetKeys: newKeys,
        // Hold Time only — every other category leaves it out (stored set untouched).
        ...(htTargets ? { holdTimeTargets: htTargets } : {}),
        // The ticked trigger forms — without this the Triggers tab looked saved but
        // dbo.AI_NotificationRuleTrigger kept the old set, so the rule never fired there.
        triggers: isHoldTime ? [] : selectedTriggerIds,
        config: JSON.stringify(cfg),
        notifyInApp: chan.inApp,
        notifyEmail: chan.email,
        autoResolve: del.autoResolve,
        delayMode: DELAY_STORED[delay],
        businessHoursOnly: bizHours,
        suppressDuplicates: del.suppress,
        clientVisible: del.portal,
        repeatEnabled: repeat.on,
        repeatEvery: Number(repeat.value) || undefined,
        repeatUnit: repeat.unit,
        // Untouched = keep the stored link; changed to "No escalation" = '' (clears it); else the group id.
        escalationGroupId: esc.groupId === (rule.escalationGroupId ?? '') ? (rule.escalationGroupId ?? null) : esc.groupId,
        escalationUserIds: esc.userIds,
        escalateAfter: esc.after,
      });
      setIsEditing(false);
      notify.success('Saved successfully.');
    });
  }
  function deleteRule() {
    confirm({ title: 'Delete rule?', message: `Delete “${rule.name}”? This cannot be undone.`, tone: 'danger', confirmLabel: 'Delete' })
      .then((ok) => { if (ok) { notify.success('Rule deleted.'); onDeleted(); } });
  }
  // Duplicate: a real copy, created by the server (which mints its code). It is saved TURNED OFF — an
  // active copy would send every alert twice — and without the original's change history.
  async function duplicateRule() {
    const current = useNcStore.getState().categories.flatMap((c) => c.rules).find((r) => r.code === rule.code) ?? rule;
    const ok = await confirm({
      title: 'Duplicate rule',
      message: `Create a copy of “${current.name}”? The copy is saved turned off, so it sends nothing until you turn it on.`,
    });
    if (!ok) return;
    const cfg: NcRuleConfig = { ...parseRuleConfig(current) };
    delete cfg.history;
    const copy: NcRule = {
      ...current, code: '', name: copyName(current.name), enabled: false,
      config: JSON.stringify(cfg),
    };
    const saved = await useNcStore.getState().createRule(categoryName, copy);
    if (saved) notify.success(`Rule duplicated as “${saved.name}”. It is turned off — open it from the rule list to review it and turn it on.`);
  }
  const refreshRule = () => onRefresh?.();

  // Rule Detail action-bar shortcuts (CLAUDE.md standing rule 14, §18.6) — wired
  // to the real edit-gate handlers: view actions gated to view mode, Save to edit
  // mode, Esc cancels an in-progress edit else navigates back.
  useHotkeys([
    { combo: 'alt+shift+c', label: 'Duplicate', group: 'Rule Detail', order: 1, disabled: isEditing, run: duplicateRule },
    { combo: 'alt+e', label: 'Edit', group: 'Rule Detail', order: 2, disabled: isEditing, run: startEdit },
    { combo: 'alt+d', label: 'Delete', group: 'Rule Detail', order: 3, disabled: isEditing, run: deleteRule },
    { combo: 'alt+r', label: 'Refresh', group: 'Rule Detail', order: 4, disabled: isEditing, run: refreshRule },
    { combo: 'mod+s', label: 'Save', group: 'Rule Detail', order: 5, disabled: !isEditing, run: saveEdit },
    { combo: 'esc', label: isEditing ? 'Cancel' : 'Back', group: 'Rule Detail', order: 6, allowInInput: false, run: () => (isEditing ? cancelEdit() : onBack()) },
  ]);

  return (
    <div className="nc-root">
      <div className="nc-topbar">
        <div className="nc-title-wrap">
          <div className="nc-title-ic"><i className="ti ti-adjustments-horizontal" /></div>
          <div><div className="nc-title">Rule Detail</div><div className="nc-sub">{name} · {rule.code}</div></div>
          <span className="nc-load"><i className="ti ti-bolt" /> Loaded in 0.3 sec</span>
          {/* The shared, config-gated Audit chip — this rule's dbo.AuditData rows (FormName "Notification Rules"). */}
          <AuditTrailButton variant="chip" formName={NotificationRulesAuditForm} sourceId={rule.oid || undefined} />
        </div>
        <div className="nc-actions">
          {isEditing ? (
            <>
              <button className="ca-btn" onClick={cancelEdit} title={`Cancel (${formatCombo('esc')})`}><i className="ti ti-x" /> Cancel</button>
              <button className="ca-btn ca-btn-solid" title={`Save (${formatCombo('mod+s')})`} onClick={saveEdit}><span className="tb-i">💾</span> Save</button>
            </>
          ) : (
            <>
              <button className="ca-btn" onClick={duplicateRule} title={`Duplicate (${formatCombo('alt+shift+c')})`}><i className="ti ti-copy" /> Duplicate</button>
              <button className="ca-btn" onClick={startEdit} title={`Edit (${formatCombo('alt+e')})`}><span className="tb-i tb-i-blue">✎</span> Edit</button>
              <button className="ca-btn" onClick={deleteRule} title={`Delete (${formatCombo('alt+d')})`}><span className="tb-i tb-i-red">🗑</span> Delete</button>
              <button className="ca-btn" onClick={refreshRule} title={`Refresh (${formatCombo('alt+r')})`}><span className="tb-i tb-i-green">🔄</span> Refresh</button>
            </>
          )}
        </div>
      </div>

      <div className="nc-scroll">
        {/* detail header */}
        <div className="nc-dhead">
          <div className="nc-dcrumb" onClick={onBack}><i className="ti ti-arrow-left" /> Notification Rules</div>
          <div className="nc-dhead-top">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                {rule.outbound ? <span className="nc-comm"><span className="dot" />Outbound</span> : severity ? <SevPill s={severity} /> : null}
                {savedAck && <span className="nc-acktag">Ack required</span>}
              </div>
              <div className="nc-dname">{name}</div>
            </div>
            <div className="nc-dstatus">
              <span className={`nc-toggle${active ? ' on' : ''}${isEditing ? '' : ' nc-ro'}`} role="button" onClick={isEditing ? toggleActive : undefined} />
              <div><div className="nc-dsb-label">{active ? 'Active' : 'Disabled'}</div><div className="nc-dsb-sub">{active ? 'Evaluating live' : 'Not evaluating'}</div></div>
            </div>
          </div>
        </div>

        {/* tabs */}
        <div className="nc-tabs">
          {visibleTabs.map((t) => (
            <div key={t.key} className={`nc-tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
              <i className={`ti ${t.icon}`} /> {t.label}
              {t.key === 'targets' && <span className="nc-tab-count">{enabledCount}</span>}
              {t.key === 'recipients' && <span className="nc-tab-count">{recipientGroups.length}</span>}
            </div>
          ))}
        </div>

        <Fragment key={formKey}>
        {/* GENERAL */}
        {tab === 'general' && (
          <>
            <div className="nc-panel">
              <div className="nc-panel-hd"><div className="nc-panel-title"><i className="ti ti-info-circle" /> Rule Definition</div></div>
              <div className="nc-panel-body">
                <div className="nc-infogrid">
                  <Info label="Rule Name">{isEditing
                    ? <input className="nc-finput" value={name} onChange={(e) => setName(e.target.value)} placeholder="Rule name" />
                    : name}</Info>
                  <Info label="Rule Code"><span className="nc-mono">{rule.code}</span></Info>
                  <Info label="Category">{categoryName}</Info>
                  <Info label="Notification Purpose"><span className={`nc-pp ${rule.purpose}`}>{PURPOSE_LABEL[rule.purpose]}</span></Info>
                  <Info label="Severity">{rule.outbound
                    ? <span className="nc-comm"><span className="dot" />Outbound</span>
                    : isEditing
                      ? <select className="nc-fselect nc-sm" value={severity ?? ''} onChange={(e) => setSeverity(e.target.value as Severity)}>
                          {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{SEVERITY_LABEL[s]}</option>)}
                        </select>
                      : severity ? <SevPill s={severity} /> : <span className="nc-comm"><span className="dot" />Outbound</span>}</Info>
                </div>
              </div>
            </div>
            <div className="nc-panel">
              <div className="nc-panel-hd"><div className="nc-panel-title"><i className="ti ti-file-description" /> Description</div></div>
              <div className="nc-panel-body" style={{ fontSize: 12.5, color: 'var(--nc-text-mid)', lineHeight: 1.6 }}>
                {/* By category CODE, and the SAVED acknowledgment setting. */}
                {ruleDescription(ruleCategoryCode, savedAck)}
              </div>
            </div>
          </>
        )}

        {/* TRIGGER — not shown for a Hold Time (RC-02) rule: it is raised by the background hold-time scan, and its
            tests + Warn Before times are on the Targets tab (owner request 2026-10-23: the two tabs showed the same). */}
        {tab === 'trigger' && !isHoldTime && (
          <div className="nc-panel">
            <div className="nc-panel-body">
              {disabledSelected.length > 0 && (
                <div className="nc-form-hint" style={{ color: 'var(--nc-warn)', marginTop: 0, marginBottom: 12 }}>
                  <i className="ti ti-alert-triangle" style={{ marginRight: 4 }} />
                  {disabledSelected.length === 1 ? 'A saved trigger' : 'Saved triggers'} <strong>{disabledSelected.map((f) => f.label).join(', ')}</strong> {disabledSelected.length === 1 ? 'is' : 'are'} currently <strong>disabled</strong> in Default Settings — that event will not fire. The rule's saved configuration is kept unchanged; re-enable the form in Default Settings, or turn it off here and Save.
                </div>
              )}
              {isPermitExceedance && (
                <div className="nc-form-group">
                  <label className="nc-flabel">Which parameters should this rule monitor?</label>
                  <div className="nc-form-hint" style={{ margin: '0 0 10px' }}>Every parameter from the <strong>Permit Library</strong> with its limits — the source of truth. Expand a row to see its configured limits. The rule fires when <strong>any</strong> enabled parameter’s result is outside its limit on one of the trigger forms below.{isEditing ? ' Turn parameters on or off; Save to apply, Cancel to discard.' : ' Click Edit to change them.'}</div>
                  {permitGrid}
                </div>
              )}
              <div className="nc-form-group">
                <label className="nc-flabel">Trigger forms / events</label>
                <div className="nc-form-hint" style={{ margin: '0 0 8px' }}>Turn on one or more. The rule evaluates when the applicable record reaches <strong>any</strong> enabled event (once per record — duplicates suppressed).</div>
                <table className="nc-tgt-table">
                  <thead><tr><th>Form Name</th><th style={{ width: 90, textAlign: 'center' }}>Enabled</th></tr></thead>
                  <tbody>
                    {triggerRows.map((f) => (
                      <tr key={f.id}>
                        <td><div className="nc-tgt-name"><i className={`ti ${f.icon}`} style={{ marginRight: 6, color: 'var(--nc-text-mid)' }} />{f.label}{!f.enabled && <span className="nc-oc-off"> · disabled</span>}</div><div className="nc-tgt-scope">{f.sub}</div></td>
                        <td style={{ textAlign: 'center' }}>
                          {/* Enabled changes ONLY from the toggle itself — the row is not clickable. */}
                          <span className={`nc-toggle${triggerSel[f.id] ? ' on' : ''}${isEditing ? '' : ' nc-ro'}`} role="button" aria-pressed={!!triggerSel[f.id]}
                            onClick={isEditing ? () => setTriggerSel((cur) => ({ ...cur, [f.id]: !cur[f.id] })) : undefined} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="nc-infogrid">
                <Info label="Watches">Analysis Result</Info>
                <Info label="Triggers">{selectedTriggerIds.length ? triggerRows.filter((f) => triggerSel[f.id]).map((f) => f.label).join(', ') : '—'}</Info>
                <Info label="Match mode">Any target matches (OR)</Info>
              </div>
              <div className="nc-form-hint" style={{ marginTop: 12 }}><i className="ti ti-bolt" style={{ color: 'var(--nc-orange)', marginRight: 4 }} />{triggersEvaluationText(selectedTriggerIds)}</div>
            </div>
          </div>
        )}

        {/* TARGETS — Permit Library master/detail grid (View = enabled only; Edit = all) */}
        {tab === 'targets' && (
          <>
            <div className="nc-panel">
              <div className="nc-panel-hd">
                <div>
                  <div className="nc-panel-title"><i className="ti ti-target" /> Monitored Targets</div>
                  <div className="nc-panel-sub">{isHoldTime
                    ? (isEditing
                      ? <>Turn <strong>Alert</strong> on for each test to monitor and set its <strong>Warn Before</strong> times. Every test with a configured hold time is listed. Save to apply, Cancel to discard.</>
                      : <>The tests this rule monitors, with their hold time and Warn Before times. Click <strong>Edit</strong> to change them.</>)
                    : isEditing
                    ? <>Turn parameters on or off. Every parameter from the <strong>Permit Library</strong> is listed — only enabled ones are monitored. Save to apply, Cancel to discard.</>
                    : <>The parameters this rule monitors. Expand a row to see its configured Permit Library limits. Click <strong>Edit</strong> to change the selection.</>}
                  </div>
                </div>
                {!isHoldTime && <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontSize: 11, color: 'var(--nc-text-light)' }}>Match mode:</span>
                  <select className="nc-fselect nc-sm" disabled={!isEditing}><option>Any target matches (OR)</option><option>All targets match (AND)</option></select>
                </div>}
              </div>
              {isHoldTime ? holdTimeGrid : permitGrid}
            </div>
            <div className="nc-form-hint" style={{ padding: '0 4px' }}>{isHoldTime
              ? <>Hold times read live from each test's Test Guide — Before Prep and Before Analysis separately. Each Warn Before time (days, hours or minutes) must be less than its own hold time.</>
              : isEditing
              ? <>Limits read live from the <strong>Permit Library</strong> — Limit Type, Operator, Limit, Units and effective dates are never re-entered here. A parameter with several limit types (e.g. Fluoride MCL + SMCL) appears once; expand it to see all.</>
              : <>Showing the <strong>{enabledCount}</strong> enabled of {permitTargets.length} available Permit Library parameters. Limits read live from the Permit Library.</>}
            </div>
          </>
        )}

        {/* RECIPIENTS */}
        {tab === 'recipients' && (
          <div className="nc-panel">
            <div className="nc-panel-hd">
              <div><div className="nc-panel-title"><i className="ti ti-users-group" /> Recipients</div><div className="nc-panel-sub"><strong>The setting you configure for this lab.</strong> Which <strong>Recipient Groups</strong> are notified when this rule fires — each group's roles, users and channels resolve from its own configuration.{isEditing ? ' Check a group to add it, uncheck to remove.' : ' Click Edit to change.'}</div></div>
            </div>
            <div className="nc-panel-body">
              {/* Recipient GROUP picker — identical here and in the New Rule
                  wizard's Recipients step. Groups load live from the DB. */}
              <RecipientGroupPicker selected={recipientGroups} onChange={setRecipientGroups} readOnly={!isEditing} known={rule.recipientGroups} />
            </div>
          </div>
        )}

        {/* DELIVERY */}
        {tab === 'delivery' && (
          <div className="nc-panel nc-delivery-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-send" /> Delivery Method</div><div className="nc-panel-sub">How and when the notification is delivered.</div></div></div>
            <div className="nc-panel-body">
              <DeliveryMethodFields value={deliveryValue} onChange={setDelivery} disabled={!isEditing}
                showSuppress={isPermitExceedance} showPortal={!isHoldTime}
                escalation={{ value: esc, onChange: setEsc }} />
            </div>
          </div>
        )}

        {/* ESCALATION — inside Delivery Method → In-App alert since 2026-10-23 (owner request). */}

        {/* TEMPLATE */}
        {tab === 'template' && (
          <div className="nc-panel">
            <div className="nc-panel-hd"><div><div className="nc-panel-title"><i className="ti ti-mail" /> Email Template</div><div className="nc-panel-sub">The template used to render this rule's email. Manage templates on the Email Templates screen.</div></div></div>
            <div className="nc-panel-body">
              <EmailTemplatePicker value={emailTemplate} onChange={setEmailTemplate} readOnly={!isEditing} />
            </div>
          </div>
        )}

        </Fragment>
      </div>
    </div>
  );
}
